<script lang="ts">
  import { onMount } from 'svelte';
  import * as XLSX from 'xlsx';
  import Icon from './Icon.svelte';
  import {
    fetchPublicDataTables,
    bulkImportRecords,
    type ApiTable,
    type ApiField,
  } from '../lib/data-tables-api';
  import { bulkImportPersons, bulkImportPayments } from '../lib/admin-api';
  import { fieldType } from '../lib/data-tables-catalog';
  import ImportRunsHistory from './ImportRunsHistory.svelte';

  /** Активна вкладка: 'flow' = імпорт файлу, 'history' = persistent історія. */
  let tab = $state<'flow' | 'history'>('flow');

  /** Якщо передано — таблиця в picker'і заблокована, скролимо одразу до кроку 2. */
  let { focusSlug }: { focusSlug?: string } = $props();

  let tables = $state<ApiTable[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  let selectedSlug = $state<string>('');
  let format = $state<'csv' | 'xlsx'>('csv');
  let fileName = $state<string>('');
  let parsing = $state(false);

  /** Обмеження. Має відповідати backend (express.json limit: 20mb + Zod
   *  max 10000 rows у /bulk-import endpoint'ах). */
  const MAX_FILE_MB = 20;
  const MAX_ROWS = 10000;

  /** parsedRows[0] = headers, parsedRows[1+] = data. */
  let parsedRows = $state<string[][]>([]);
  /** header → fieldKey (або '' щоб пропустити). */
  let mapping = $state<Record<string, string>>({});

  let importing = $state(false);
  let progressText = $state<string>('');
  let result = $state<{
    imported: number;
    total: number;
    skipped: number;
    duplicatesSkipped?: number;
    /** Скільки рядків додалось як НОВІ Person'и (users-table only). */
    created?: number;
    /** Скільки existing Person'ів збагатились даними (users-table only). */
    enriched?: number;
    /** Скільки рядків спричинили merge двох+ Person'ів у один. */
    merged?: number;
    invalid?: number;
    errors?: { row: number; reason: string }[];
    log?: ImportLogEntry[];
  } | null>(null);

  interface ImportLogEntry {
    row: number;
    action: 'created' | 'enriched' | 'merged' | 'skipped' | 'failed';
    personId?: string;
    mergedPersonIds?: string[];
    matchedBy?: string[];
    identifiers?: {
      email?: string;
      phone?: string;
      telegramId?: string;
      sendpulseContactId?: string;
    };
    message?: string;
    error?: string;
  }

  /** Скільки рядків відправляти за один POST. Нижчий → менше шансів на
   *  504/timeout від nginx (default proxy_read_timeout 60s), вищий →
   *  менше HTTP overhead. 500 — комфортний компроміс. */
  const CHUNK_SIZE = 500;

  /** Які таблиці підтримуються: built-in users + payments + всі custom. */
  function isImportable(t: ApiTable): boolean {
    if (!t.builtIn) return true;
    return t.slug === 'users' || t.slug === 'payments';
  }

  async function load() {
    loading = true;
    err = null;
    try {
      const r = await fetchPublicDataTables();
      tables = r.items;
      const importable = tables.filter(isImportable);
      if (focusSlug && importable.some((t) => t.slug === focusSlug)) {
        selectedSlug = focusSlug;
      } else if (importable.length > 0) {
        selectedSlug = importable[0]!.slug;
      }
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);

  const importableTables = $derived(tables.filter(isImportable));
  const selectedTable = $derived(tables.find((t) => t.slug === selectedSlug) ?? null);
  const headers = $derived(parsedRows.length > 0 ? parsedRows[0] ?? [] : []);
  const dataRows = $derived(parsedRows.slice(1));
  const previewRows = $derived(dataRows.slice(0, 10));

  function resetFile() {
    parsedRows = [];
    mapping = {};
    fileName = '';
    result = null;
  }

  function onSelectTable() {
    resetFile();
  }
  function onFormatChange(fmt: 'csv' | 'xlsx') {
    format = fmt;
    resetFile();
  }

  /**
   * Читає CSV як текст. Спершу пробує UTF-8; якщо у результаті багато
   * Unicode-replacement-чарів (�) — fallback на windows-1251 (Excel у
   * Windows зазвичай зберігає кирилицю саме так).
   */
  async function readAsUtf8(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    // Strict decode щоб ловити невалідні UTF-8 байти.
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(buf);
      return text;
    } catch {
      // Невалідний UTF-8 → пробуємо windows-1251.
      return new TextDecoder('windows-1251').decode(buf);
    }
  }

  async function onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];
    if (!f) return;
    // Hard-limit на розмір файлу.
    const sizeMb = f.size / (1024 * 1024);
    if (sizeMb > MAX_FILE_MB) {
      err = `Файл завеликий: ${sizeMb.toFixed(1)} MB. Максимум — ${MAX_FILE_MB} MB. Розбийте на декілька менших.`;
      input.value = '';
      return;
    }
    parsing = true;
    err = null;
    result = null;
    fileName = f.name;
    try {
      // CSV читаємо як UTF-8 текст і парсимо як string — інакше xlsx
      // інтерпретує байти як Latin-1 (CP1252) і кирилиця стає кракозябрами
      // (Ð¾Ð¼ÐµÑ€ замість "Номер"). Якщо у CSV є BOM — TextDecoder сам
      // його пропустить. Для XLSX (binary) кодування лежить у самому файлі.
      const wb = f.name.toLowerCase().endsWith('.csv')
        // raw:true вимикає auto-парсинг "схожих на дати" клітинок у Excel-
        // serial (інакше xlsx-js інтерпретує "12.05.2026" як US-mm/dd → Dec 5).
        ? XLSX.read(await readAsUtf8(f), { type: 'string', raw: true })
        : XLSX.read(await f.arrayBuffer(), { type: 'array', raw: true, cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]!];
      if (!sheet) throw new Error('Файл порожній або не містить аркушів');
      // header:1 → 2D array; defval keeps empty cells as '' (а не undefined).
      const arr = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: true });
      const rows = (arr as unknown[][]).map((r) =>
        r.map((c) => {
          if (c == null) return '';
          // Date-об'єкти з XLSX (cellDates:true) серіалізуємо у ISO —
          // інакше String(date) даєт locale-залежний рядок який важко
          // парсити одноманітно.
          if (c instanceof Date) return c.toISOString();
          return String(c);
        }),
      );
      // Прибираємо повністю порожні рядки (хвостові).
      while (rows.length > 0 && rows[rows.length - 1]!.every((c) => c.trim() === '')) {
        rows.pop();
      }
      if (rows.length === 0) throw new Error('Файл не містить даних');
      // rows[0] = header, тож реальні data-рядки = rows.length - 1.
      if (rows.length - 1 > MAX_ROWS) {
        throw new Error(`Забагато рядків: ${rows.length - 1}. Максимум — ${MAX_ROWS} за один імпорт. Розбийте файл на частини.`);
      }
      parsedRows = rows;
      // Авто-маппінг — якщо header збігається з fieldKey або label таблиці.
      const auto: Record<string, string> = {};
      if (selectedTable) {
        const hdr = rows[0]!;
        for (const h of hdr) {
          const norm = h.trim().toLowerCase();
          const matched = selectedTable.fields.find(
            (f) =>
              f.fieldKey.toLowerCase() === norm ||
              f.label.toLowerCase() === norm,
          );
          auto[h] = matched?.fieldKey ?? '';
        }
      }
      mapping = auto;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
      parsedRows = [];
      mapping = {};
    } finally {
      parsing = false;
    }
  }

  function setColumnMapping(header: string, fieldKey: string) {
    mapping = { ...mapping, [header]: fieldKey };
  }

  const mappedCount = $derived(
    Object.values(mapping).filter((v) => v && v !== '').length,
  );

  async function doImport() {
    if (!selectedSlug || dataRows.length === 0) return;
    const mappedHeaders = headers.filter((h) => mapping[h] && mapping[h] !== '');
    if (mappedHeaders.length === 0) {
      err = 'Замапте хоча б одне поле';
      return;
    }
    importing = true;
    err = null;
    progressText = '';
    result = null;

    try {
      const rows: Record<string, unknown>[] = dataRows.map((row) => {
        const o: Record<string, unknown> = {};
        headers.forEach((h, idx) => {
          const fk = mapping[h];
          if (!fk) return;
          const v = row[idx];
          if (v != null && v !== '') o[fk] = v;
        });
        return o;
      });

      // Розбиваємо на чанки щоб не вперлись у nginx-timeout (504).
      const chunks: Record<string, unknown>[][] = [];
      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        chunks.push(rows.slice(i, i + CHUNK_SIZE));
      }

      // Accumulator для всіх чанків.
      const acc = {
        imported: 0,
        total: rows.length,
        skipped: 0,
        duplicatesSkipped: 0,
        created: 0,
        enriched: 0,
        merged: 0,
        invalid: 0,
        errors: [] as { row: number; reason: string }[],
        log: [] as ImportLogEntry[],
      };

      const send = selectedSlug === 'users'
        ? bulkImportPersons
        : selectedSlug === 'payments'
        ? bulkImportPayments
        : (r: Record<string, unknown>[]) => bulkImportRecords(selectedSlug, r);

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i]!;
        progressText = `Чанк ${i + 1} / ${chunks.length} · ${acc.imported} імпортовано…`;
        const r = await send(chunk);
        acc.imported += r.imported ?? 0;
        acc.skipped += r.skipped ?? 0;
        acc.duplicatesSkipped += (r as { duplicatesSkipped?: number }).duplicatesSkipped ?? 0;
        acc.created += (r as { created?: number }).created ?? 0;
        acc.enriched += (r as { enriched?: number }).enriched ?? 0;
        acc.merged += (r as { merged?: number }).merged ?? 0;
        acc.invalid += (r as { invalid?: number }).invalid ?? 0;
        const offset = i * CHUNK_SIZE;
        // Per-row log: зміщуємо row-номера на offset чанка, обмежуємо
        // загалом до 500 рядків у відображенні.
        const chunkLog = (r as { log?: ImportLogEntry[] }).log ?? [];
        for (const e of chunkLog) {
          if (acc.log.length >= 500) break;
          acc.log.push({ ...e, row: e.row + offset });
        }
        const chunkErrors = (r as { errors?: { row: number; reason: string }[] }).errors ?? [];
        for (const e of chunkErrors.slice(0, Math.max(0, 20 - acc.errors.length))) {
          acc.errors.push({ row: e.row + offset, reason: e.reason });
        }
      }
      result = acc;
      progressText = '';
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      importing = false;
    }
  }
</script>

<header class="mb-5">
  <h1 class="text-xl font-semibold leading-tight">Імпорт даних</h1>
  <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
    Завантажте CSV / XLSX і прив'яжіть колонки до полів кастомної таблиці.
    Built-in таблиці (Користувачі / Платежі) поки що не підтримуються.
  </p>
</header>

<!-- Top tabs -->
<div class="mb-4 flex gap-1 border-b border-[var(--color-border-3)]">
  <button
    class="px-4 py-2 text-xs font-semibold border-b-2 -mb-[1px] {tab === 'flow' ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}"
    onclick={() => (tab = 'flow')}
  ><span class="inline-flex items-center gap-1.5"><Icon name="download" size={14} /> Імпорт</span></button>
  <button
    class="px-4 py-2 text-xs font-semibold border-b-2 -mb-[1px] {tab === 'history' ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}"
    onclick={() => (tab = 'history')}
  ><span class="inline-flex items-center gap-1.5"><Icon name="listView" size={14} /> Історія</span></button>
</div>

{#if tab === 'history'}
  <ImportRunsHistory />
{:else if loading}
  <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
{:else if importableTables.length === 0}
  <div class="rounded-xl border border-dashed border-[var(--color-border-3)] bg-white p-8 text-center text-sm text-[var(--color-text-muted)]">
    Поки що немає жодної кастомної таблиці. Спочатку створіть її в
    <a class="underline" href="#/settings/data-structure">Управління таблицями</a>.
  </div>
{:else}
  <div class="space-y-5">
    <!-- Step 1: target table -->
    <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
      <div class="mb-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">
        Крок 1 · Куди імпортувати
      </div>
      <select
        bind:value={selectedSlug}
        onchange={onSelectTable}
        disabled={!!focusSlug}
        class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm disabled:bg-[var(--color-bg-3)]"
      >
        {#each importableTables as t (t.slug)}
          <option value={t.slug}>{t.name} ({t.fields.length} полів)</option>
        {/each}
      </select>
    </section>

    <!-- Step 2: format + file -->
    <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
      <div class="mb-3 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">
        Крок 2 · Формат і файл
      </div>
      <div class="mb-3 flex gap-2">
        <button
          type="button"
          onclick={() => onFormatChange('csv')}
          class="rounded-md border px-3 py-1.5 text-xs font-semibold"
          class:border-[var(--color-pu-3)]={format === 'csv'}
          class:bg-[var(--color-pu-light)]={format === 'csv'}
          class:text-[var(--color-pu-3)]={format === 'csv'}
          class:border-[var(--color-border-3)]={format !== 'csv'}
        >.CSV</button>
        <button
          type="button"
          onclick={() => onFormatChange('xlsx')}
          class="rounded-md border px-3 py-1.5 text-xs font-semibold"
          class:border-[var(--color-pu-3)]={format === 'xlsx'}
          class:bg-[var(--color-pu-light)]={format === 'xlsx'}
          class:text-[var(--color-pu-3)]={format === 'xlsx'}
          class:border-[var(--color-border-3)]={format !== 'xlsx'}
        >.XLSX</button>
      </div>

      <label class="block rounded-lg border-2 border-dashed border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-6 text-center cursor-pointer hover:bg-[var(--color-pu-light)] transition">
        <input
          type="file"
          accept={format === 'csv' ? '.csv,text/csv' : '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
          onchange={onFileChange}
          class="hidden"
        />
        {#if parsing}
          <div class="text-sm text-[var(--color-text-muted)]">Парсимо файл…</div>
        {:else if fileName}
          <div class="mb-1 inline-flex text-emerald-600"><Icon name="statusDone" size={28} /></div>
          <div class="text-sm font-semibold mb-0.5">{fileName}</div>
          <div class="text-[11px] text-[var(--color-text-muted-2)]">
            {dataRows.length} рядків даних · {headers.length} колонок · клік щоб замінити
          </div>
        {:else}
          <div class="mb-1 inline-flex text-[var(--color-text-muted)]"><Icon name="download" size={28} /></div>
          <div class="text-sm font-semibold mb-0.5">Оберіть {format.toUpperCase()} файл</div>
          <div class="text-[11px] text-[var(--color-text-muted-2)]">
            або перетягніть сюди
          </div>
        {/if}
      </label>
      <div class="mt-2 text-[10.5px] text-[var(--color-text-muted-2)]">
        Обмеження: до <strong>{MAX_FILE_MB} MB</strong> · до <strong>{MAX_ROWS.toLocaleString('uk-UA')}</strong> рядків за один імпорт. Більший файл — розбийте на частини.
      </div>
    </section>

    {#if parsedRows.length > 0 && selectedTable}
      <!-- Step 3: column mapping -->
      <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
        <div class="mb-3 flex items-center justify-between">
          <div class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">
            Крок 3 · Прив'язка колонок ({mappedCount}/{headers.length})
          </div>
        </div>

        {#if selectedSlug === 'payments'}
          <div class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
            <strong>Обовʼязково замапте:</strong>
            <strong>Сума ₴</strong>,
            <strong>Статус</strong> (або <strong>Raw status</strong> з "Approved/Expired/..."),
            <strong>Час платежу</strong> (або <strong>Час запиту</strong>),
            <strong>Email або Телефон платника</strong> (для звʼязку з Person).
          </div>
        {:else if selectedSlug === 'users'}
          <div class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
            <strong>Обовʼязково замапте</strong> хоча б одне з:
            <strong>Email</strong> / <strong>Телефон</strong> / <strong>Telegram ID</strong> / <strong>SendPulse contact ID</strong>.
          </div>
        {/if}

        <div class="overflow-x-auto rounded-md border border-[var(--color-border-3)]">
          <table class="w-full text-xs">
            <thead class="bg-[var(--color-bg-3)]">
              <tr class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)]">
                {#each headers as h, i (i)}
                  <th class="text-left px-2 py-2 font-bold min-w-[150px]">
                    <div class="mb-1 truncate" title={h}>{h || `Col ${i + 1}`}</div>
                    <select
                      value={mapping[h] ?? ''}
                      onchange={(e) => setColumnMapping(h, (e.target as HTMLSelectElement).value)}
                      class="w-full rounded border border-[var(--color-border-3)] bg-white px-1.5 py-1 text-[10px] font-medium normal-case tracking-normal"
                    >
                      <option value="">— Пропустити —</option>
                      {#each selectedTable.fields as f (f.id)}
                        <option value={f.fieldKey}>{f.label}</option>
                      {/each}
                    </select>
                  </th>
                {/each}
              </tr>
            </thead>
            <tbody>
              {#each previewRows as row, ri (ri)}
                <tr class="border-t border-[var(--color-border-3)]">
                  {#each row as cell, ci (ci)}
                    <td class="px-2 py-1.5 text-[11.5px] align-top max-w-[200px] truncate">{cell || '—'}</td>
                  {/each}
                </tr>
              {/each}
              {#if dataRows.length > previewRows.length}
                <tr>
                  <td colspan={headers.length} class="px-2 py-2 text-center text-[10.5px] text-[var(--color-text-muted-2)]">
                    …і ще {dataRows.length - previewRows.length} рядків
                  </td>
                </tr>
              {/if}
            </tbody>
          </table>
        </div>
      </section>

      <!-- Step 4: import -->
      <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
        <div class="mb-3 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">
          Крок 4 · Імпорт
        </div>
        {#if result}
          <div class="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 mb-3">
            <div class="inline-flex items-center gap-1.5 font-bold mb-1"><Icon name="statusDone" size={15} /> Імпорт завершено</div>
            <div class="text-[12px]">
              Опрацьовано <strong>{result.imported}</strong> з {result.total} рядків
              {#if (result.created ?? 0) > 0 || (result.enriched ?? 0) > 0 || (result.merged ?? 0) > 0}
                <br />
                <span class="inline-flex flex-wrap items-center gap-x-1 text-emerald-700">
                  <Icon name="add" size={13} class="inline align-[-2px]" /> нових: <strong>{result.created ?? 0}</strong>
                  · <Icon name="refresh" size={13} class="inline align-[-2px]" /> збагачено: <strong>{result.enriched ?? 0}</strong>
                  · <Icon name="url" size={13} class="inline align-[-2px]" /> змерджено: <strong>{result.merged ?? 0}</strong>
                </span>
              {/if}
              {#if (result.duplicatesSkipped ?? 0) > 0}
                · пропущено дублів: <strong>{result.duplicatesSkipped}</strong>
              {/if}
              {#if (result.invalid ?? 0) > 0}
                · невалідних: <strong>{result.invalid}</strong>
              {/if}
            </div>
            {#if result.errors && result.errors.length > 0}
              <details class="mt-2 text-[11.5px]">
                <summary class="cursor-pointer font-semibold">Показати помилки ({result.errors.length})</summary>
                <ul class="mt-1.5 ml-4 list-disc space-y-0.5">
                  {#each result.errors as e (e.row)}
                    <li>Рядок {e.row}: {e.reason}</li>
                  {/each}
                </ul>
              </details>
            {/if}

            {#if result.log && result.log.length > 0}
              {@const log = result.log}
              <details class="mt-2 text-[11.5px]" open={log.some((l) => l.action === 'failed' || l.action === 'merged')}>
                <summary class="cursor-pointer font-semibold">Детальний лог по рядках ({log.length})</summary>
                <div class="mt-2 max-h-[500px] overflow-y-auto rounded border border-emerald-200 bg-white text-[11px]">
                  <table class="w-full">
                    <thead class="sticky top-0 bg-[var(--color-bg-3)] text-[9.5px] uppercase tracking-wider text-[var(--color-text-muted)]">
                      <tr>
                        <th class="text-left px-2 py-1.5 w-12">#</th>
                        <th class="text-left px-2 py-1.5 w-20">Дія</th>
                        <th class="text-left px-2 py-1.5">Identifiers</th>
                        <th class="text-left px-2 py-1.5">Деталі</th>
                      </tr>
                    </thead>
                    <tbody>
                      {#each log as l (l.row)}
                        <tr class="border-t border-[var(--color-border-3)] align-top">
                          <td class="px-2 py-1.5 text-[var(--color-text-muted)] tabular-nums">{l.row}</td>
                          <td class="px-2 py-1.5">
                            <span class="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider {
                              l.action === 'created' ? 'bg-emerald-100 text-emerald-700' :
                              l.action === 'enriched' ? 'bg-blue-100 text-blue-700' :
                              l.action === 'merged' ? 'bg-purple-100 text-purple-700' :
                              l.action === 'failed' ? 'bg-rose-100 text-rose-700' :
                              'bg-slate-100 text-slate-500'
                            }">{l.action}</span>
                          </td>
                          <td class="px-2 py-1.5 font-mono text-[10px] text-slate-700">
                            {#if l.identifiers}
                              {#if l.identifiers.telegramId}<div>tg={l.identifiers.telegramId}</div>{/if}
                              {#if l.identifiers.sendpulseContactId}<div>sp={l.identifiers.sendpulseContactId.slice(0, 12)}…</div>{/if}
                              {#if l.identifiers.email}<div>email={l.identifiers.email}</div>{/if}
                              {#if l.identifiers.phone}<div>phone={l.identifiers.phone}</div>{/if}
                            {/if}
                          </td>
                          <td class="px-2 py-1.5 text-slate-700">
                            {#if l.error}
                              <span class="text-rose-700">{l.error}</span>
                            {:else}
                              {#if l.personId}
                                <div class="font-mono text-[10px]">
                                  →
                                  <a href={`#/persons/${l.personId}`} class="text-[var(--color-pu-3)] hover:underline">
                                    {l.personId.slice(0, 8)}…
                                  </a>
                                </div>
                              {/if}
                              {#if l.matchedBy && l.matchedBy.length > 0}
                                <div class="text-[10px] text-slate-500">match: {l.matchedBy.join(', ')}</div>
                              {/if}
                              {#if l.mergedPersonIds && l.mergedPersonIds.length > 0}
                                <div class="text-[10px] text-purple-700">
                                  merged {l.mergedPersonIds.length} loser(s): {l.mergedPersonIds.map((id) => id.slice(0, 8)).join(', ')}
                                </div>
                              {/if}
                              {#if l.message}
                                <div class="text-[10px] text-slate-500">{l.message}</div>
                              {/if}
                            {/if}
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
                {#if result.total > log.length}
                  <div class="mt-1 text-[10px] text-[var(--color-text-muted)]">
                    Показано перших {log.length} з {result.total} рядків.
                  </div>
                {/if}
              </details>
            {/if}
            <div class="mt-2">
              <a
                href={selectedSlug === 'users'
                  ? '#/persons'
                  : selectedSlug === 'payments'
                  ? '#/payments'
                  : `#/tables/${selectedSlug}`}
                class="underline font-semibold"
              >
                Перейти до таблиці →
              </a>
            </div>
          </div>
        {/if}

        {#if err}
          <div class="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 mb-3">{err}</div>
        {/if}

        <div class="flex items-center gap-3">
          <button
            onclick={doImport}
            disabled={importing || mappedCount === 0 || dataRows.length === 0}
            class="rounded-md bg-[var(--color-pu)] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
          >
            {importing ? 'Імпортуємо…' : `Імпортувати ${dataRows.length} рядків`}
          </button>
          {#if importing && progressText}
            <span class="text-[12px] text-[var(--color-text-muted)]">{progressText}</span>
          {/if}
        </div>
        {#if dataRows.length > CHUNK_SIZE}
          <div class="mt-2 text-[10.5px] text-[var(--color-text-muted-2)]">
            Файл буде розбито на {Math.ceil(dataRows.length / CHUNK_SIZE)} пачок по {CHUNK_SIZE} рядків щоб не впертись у nginx-timeout. Не закривай вкладку до завершення.
          </div>
        {/if}
      </section>
    {/if}

    {#if err && parsedRows.length === 0}
      <div class="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">{err}</div>
    {/if}
  </div>
{/if}
